import React from 'react';
import PropTypes from 'prop-types';
import { EUR, GBP, FROM } from '../modules/constants';

const CURRENCY = {
    EUR,
    GBP
};

const PRICE_CLASS_NAME = 'price';
const PRICE_WIDE_CLASS_NAME = `${PRICE_CLASS_NAME} wide`;

/**
 * Format the price using either Intl for browsers that support it or manually for
 * older browsers.
 *
 * @function
 * @param  {string} currency currency code (EUR/GBP) currently
 * @param  {integer} value   cost in pence/cents
 * @return {string}          human readable price.
 */
function format(currency, value) {
    const options = {
        style: 'currency',
        currency
    };
    const amount = parseInt(value) / 100;

    if (typeof window !== 'undefined' && typeof window.Intl !== 'undefined') {
        return new Intl.NumberFormat('en-US', options).format(amount);
    }
    return `${CURRENCY[currency]}${amount.toFixed(2)}`;
    //return `${amount.toFixed(2)}`;
}

/**
 * Component to display prices.
 *
 * @function
 * @param       {number|object} price       either a price object or a price string.
 * @param       {array} priceList           array of prices
 * @param       {object|null} depositFee    deposit fee object
 * @param       {boolean} naked             Just return a formatted price without an HTML element
 * @constructor
 */
function Price({ price, priceList, depositFee, naked }) {
    if (!price && !priceList) {
        return null;
    }

    // We want to display a single price, if there are multiple prices we take the first item from the priceList
    // and display that if there is no price.

    const ourPrice = price || ((priceList || [])[0] || {}).price;

    // If there are no prices returned (can happen, is almost always an error) then return nothing.
    if (!ourPrice) {
        return null;
    }

    // If there's more than one price, we have a wider SVG for the background.
    const { currencyCode, value } = ourPrice;
    const className =
        (priceList || []).length > 1 ? PRICE_WIDE_CLASS_NAME : PRICE_CLASS_NAME;
    if (naked) {
        return format(currencyCode, value);
    }

    // Display the price, with optional "FROM" if there's more than one price.
    return (
        <div className={className}>
            <div className='priceTag'>
                {(priceList || []).length > 1 ? <i>{FROM}</i> : null}
                {format(currencyCode, value)}
            </div>
            {depositFee ? (
                <div className='deposit'>
                    +{depositFee.value}
                    {depositFee.currencyCode === 'GBP' ? 'p' : 'c'} deposit
                </div>
            ) : null}
        </div>
    );
}

Price.propTypes = {
    price: PropTypes.shape({
        currencyCode: PropTypes.string,
        value: PropTypes.number
    }),
    priceList: PropTypes.array,
    depositFee: PropTypes.shape({
        currencyCode: PropTypes.string,
        value: PropTypes.number
    }),
    naked: PropTypes.bool
};

export { Price };
